import { Formik, Form } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "react-bootstrap"
import { useTranslation } from "react-i18next";
import TextBox from "../../../../controls/form/TextBox";
import TextArea from "../../../../controls/form/TextArea";
import Dropdown from "../../../../controls/form/Dropdown";
import Checkbox from "../../../../controls/GenericCheckbox";
import GenericDropdown from "../../../../controls/GenericDropdown";
import Forms from "../../../../../services/forms";
import CustomConditionControl from "../CustomConditionControl";
import * as Yup from 'yup';
import { formConditionValidation, conditionKeys, removeTouchedProps } from "../ItemValidation";
import HeaderTabs from "../../controls/HeaderTabs";
import { useFormsConfig } from "../../Context";
import { useSharedSection } from "../../SharedSectionContext";

const nameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_\s]*[a-zA-Z0-9]$/;

const EditSection = ({
    formTemplateId,
    isEdit,
    isShared = false,
    defaultShowSave = false,
    section = {},
    sectionNames = [],
    show,
    allowUpdate,
    onAdd,
    onUpdate,
    onClose
}) => {
    const { templatePreferences, preferences, logAmplitudeEvent, modalContainerRef } = useFormsConfig();
    const { sharedSection } = useSharedSection();
    const { id: deploymentId } = useParams();
    const [translate] = useTranslation();

    const [showSaveButton, setShowSaveButton] = useState(defaultShowSave);
    const [selectedTab, setSelectedTab] = useState("section");
    const [isSharedSection, setIsSharedSection] = useState(false);
    const [sharedSections, setSharedSections] = useState([]);
    const [sharedSectionGroupingId, setSharedSectionGroupingId] = useState();
    const [sharedSectionVersions, setSharedSectionVersions] = useState({});

    useEffect(() => {
        if (isSharedSection) {
            Forms.getSharedSectionsWithPublishedVersions({ deploymentId }).then((res) => {
                if (!res.error && res.data) {
                    setSharedSections(res.data);
                }
            });
        }
    }, [deploymentId, isSharedSection]);

    useEffect(() => {
        if (sharedSectionGroupingId) {
            Forms.getSharedSectionVersions({
                deploymentId,
                sharedSectionGroupingId,
                size: -1,
                query: {
                    isPublished: true
                }
            }).then((res) => {
                if (!res.error && res.data) {
                    setSharedSectionVersions((prevVersions) => ({
                        ...prevVersions,
                        [sharedSectionGroupingId]: res.data.data
                    }));
                }
            });
        }
    }, [deploymentId, sharedSectionGroupingId]);

    const configList = useMemo(() => {
        if (sharedSection) {
            return [];
        }

        return [
            { _id: "section", displayValue: translate('OTM_SECTION') },
            { _id: "visible", displayValue: translate('OTM_VISIBLE') },
            { _id: "readOnly", displayValue: translate('OTM_READONLY') },
        ];
    }, [sharedSection, translate]);

    const handleSubmit = useCallback(async (values) => {
        setShowSaveButton(false);

        removeTouchedProps(["visible", "readOnly"], values);

        if (!isSharedSection) {
            delete values.sharedSectionId;
        }

        if (isEdit) {
            let response;
            if (isShared) {
                response = await Forms.updateSharedSectionVersion({
                    deploymentId,
                    sharedSectionId: section._id,
                    body: values
                });
            }
            else {
                response = await Forms.updateSection({
                    deploymentId,
                    formTemplateId,
                    sectionId: section._id,
                    body: values
                });
                logAmplitudeEvent({
                    eventType: "updatedSection",
                    eventProperties: {
                        deploymentId,
                        formTemplateId,
                        sectionName: values.name
                    }
                });
            }

            if (!response.error) {
                onUpdate();
            }
        }
        else {
            let response;
            if (isShared) {
                response = await Forms.createSharedSection({
                    deploymentId,
                    body: {
                        ...values,
                        items: section.items
                    }
                });
            }
            else {
                response = await Forms.addSection({
                    deploymentId,
                    formTemplateId,
                    body: {
                        ...values,
                        items: section.items
                    }
                });
                logAmplitudeEvent({
                    eventType: "addedSection",
                    eventProperties: {
                        deploymentId,
                        formTemplateId,
                        sectionName: values.name
                    }
                });
            }

            if (!response.error && response.data) {
                onAdd(response.data);
            }
        }
    }, [isEdit, isShared, isSharedSection, section._id, section.items, deploymentId, formTemplateId, onAdd, onUpdate, logAmplitudeEvent]);

    const handleOnChange = useCallback(() => {
        setShowSaveButton(true);
    }, []);

    // TODO [OTM-6566]: figure out how to allow an empty array in Yup for condition validation (remove if checks)
    const getErrorList = useCallback((props) => {
        const errors = {};
        for (const fieldName in props.errors || {})
            switch (fieldName) {
                case "visible":
                    if (props.values.visible.length > 0) {
                        errors.visible = true;
                    }
                    break;
                case "readOnly":
                    if (props.values.readOnly.length > 0) {
                        errors.readOnly = true;
                    }
                    break;
                default:
                    errors.section = true;
                    break;
            }

        return errors;
    }, []);

    const renderHeader = useCallback((props) => {
        return <div className="row flex-grow-1 mt-n2 align-items-center justify-content-end">
            <div className="col-12 col-lg mt-2">
                <label className="h5">{isEdit ? translate("OTM_EDIT_SECTION") : translate("OTM_ADD_SECTION")}</label>
            </div>
            <HeaderTabs
                listData={configList}
                listAttr="displayValue"
                selectedTab={selectedTab}
                listOnClick={(_id) => setSelectedTab(_id)}
                errors={getErrorList(props)}
            />
        </div>
    }, [configList, selectedTab, isEdit, getErrorList, translate]);

    // TODO [OTM-6566]: figure out how to allow an empty array in Yup for condition validation
    const formIsValid = (props) => {
        const manualErrorCheck = { ...props.errors };

        let conditionsOverride = false;

        ['visible', 'readOnly', 'validation'].forEach((key) => {
            if (props.values[key] && props.values[key].length === 0) {
                conditionsOverride = true;
                delete manualErrorCheck[key];
            }
        });

        return conditionsOverride ? Object.keys(manualErrorCheck).length === 0 : props.isValid;
    };

    const showTabText = useMemo(() => {
        if (isShared && preferences?.sectionTitleEqualsName === true) {
            return false;
        }

        if (!isShared && templatePreferences?.sectionTitleEqualsName === true) {
            return false;
        }

        return true
    }, [isShared, preferences, templatePreferences]);

    const renderSection = useCallback((props) => {
        switch (selectedTab) {
            case 'visible': {
                return <CustomConditionControl
                    label={'OTM_VISIBLE_WHEN'}
                    labelNoStatements={"OTM_VISIBLE_ALWAYS"}
                    name="visible"
                    disabled={!allowUpdate}
                    onChange={handleOnChange}
                    addNewItem={false}
                    item={section?.items ? section.items[0] : {}}
                    isForItem={false}
                    sectionId={section?._id}
                    includeCurrentSection={false}
                    conditionKeys={conditionKeys} />
            }
            case 'readOnly': {
                return <CustomConditionControl
                    label={'OTM_READONLY_WHEN'}
                    labelNoStatements={"OTM_READONLY_ALWAYS"}
                    name="readOnly"
                    disabled={!allowUpdate}
                    onChange={handleOnChange}
                    addNewItem={false}
                    item={section?.items ? section.items[0] : {}}
                    isForItem={false}
                    sectionId={section?._id}
                    includeCurrentSection={false}
                    conditionKeys={conditionKeys} />
            }
            default: {
                return <div className="row mt-n3 align-items-start">
                    {!isEdit && !isShared ? <div className='col-12 mt-3'>
                        <Checkbox
                            label={translate('OTM_SECTION_IS_SHARED')}
                            initialValue={isSharedSection}
                            onChange={setIsSharedSection}
                            disabled={!allowUpdate}
                        />
                    </div> : null}
                    {isSharedSection ? <div className='col-6 mt-3'>
                        <GenericDropdown
                            heading={translate('OTM_SECTION_SHARED_SECTIONS')}
                            options={sharedSections}
                            valueKey="sharedSectionGroupingId"
                            displayValueKey="name"
                            initialValue={sharedSectionGroupingId}
                            onSelect={(value) => { setSharedSectionGroupingId(value); props.setFieldValue('sharedSectionId', null); }}
                            disabled={!allowUpdate}
                        />
                    </div> : null}
                    {sharedSectionGroupingId && sharedSectionVersions[sharedSectionGroupingId] ? <div className='col-6 mt-3'>
                        <Dropdown
                            heading={translate('OTM_SECTION_SHARED_SECTION_VERSIONS')}
                            options={sharedSectionVersions[sharedSectionGroupingId]}
                            valueKey="_id"
                            displayValueKey="version"
                            name="sharedSectionId"
                            disabled={!allowUpdate}
                        />
                    </div> : null}
                    <div className='col-12 mt-3'>
                        <TextBox
                            label={translate('OTM_SECTION_NAME')}
                            type="text"
                            name="name"
                            disabled={!allowUpdate}
                            isRequired={true}
                            onChange={(event) => {
                                props.setFieldValue('name', event.target.value);
                                if (!showTabText) {
                                    props.setFieldValue('tabText', event.target.value);
                                }
                            }}
                        />
                    </div>
                    {showTabText ? <div className='col-12 mt-3'>
                        <TextBox label={translate('OTM_TAB_TEXT')} type="text" name="tabText" disabled={!allowUpdate} isRequired={true} />
                    </div> : null}
                    <div className='col-12 mt-3'>
                        <TextArea label={translate('OTM_DESCRIPTION')} type="text" name="description" disabled={!allowUpdate} />
                    </div>
                </div>
            }
        }
    }, [allowUpdate, selectedTab, isShared, showTabText, isSharedSection, sharedSections, sharedSectionGroupingId, sharedSectionVersions, isEdit, section, handleOnChange, translate]);

    const renderForm = useMemo(() => {
        return <Formik initialValues={{
            _id: section?._id ?? undefined,
            sharedSectionId: section?.sharedSectionId,
            name: section?.name || "",
            tabText: section?.tabText || "",
            description: section?.description || "",
            visible: section?.visible || [],
            readOnly: section?.readOnly || []
        }}
            validateOnMount
            validationSchema={Yup.object({
                name: Yup.string().matches(nameRegex, translate("OTM_INVALID_SECTION_NAME"))
                    .test("unique-name",
                        translate("OTM_SECTION_NAME_ALREAD_EXISTS"),
                        (value) => {
                            return !sectionNames.includes(value);
                        }).required(translate("OTM_SECTION_NAME_REQUIRED")),
                tabText: Yup.string().required(translate("OTM_TAB_TEXT_REQUIRED")),
                visible: formConditionValidation(translate),
                readOnly: formConditionValidation(translate)
            })}
            onSubmit={handleSubmit}
            enableReinitialize>
            {props => (<Form onChange={handleOnChange} className="flex-grow-1 d-flex flex-column">
                <Modal.Header>
                    {renderHeader(props)}
                </Modal.Header>

                <Modal.Body className="overflow-visible" style={{ minHeight: 'none', maxHeight: 'none' }}>
                    {renderSection(props)}
                </Modal.Body>

                <Modal.Footer>
                    {showSaveButton && allowUpdate && formIsValid(props) && <Button size='sm' type="submit" variant='primary'>{translate('OTM_SAVE')}</Button>}
                    <Button size='sm' variant='secondary' onClick={onClose}>{translate(allowUpdate ? 'OTM_CANCEL' : 'OTM_CLOSE')} </Button>
                </Modal.Footer>
            </Form>)}
        </Formik >
    }, [section, sectionNames, onClose, showSaveButton, allowUpdate, renderSection, renderHeader, handleOnChange, handleSubmit, translate])

    return <Modal
        container={modalContainerRef ? modalContainerRef.current : null}
        size={modalContainerRef ? 'lg' : 'xl'}
        show={show}
        backdrop={'static'}>
        {renderForm}
    </Modal>
}

export default EditSection;